import { ChevronDown } from 'lucide-react';
import { Link, useLocation } from 'react-router-dom';

import { cn } from '../lib/cn';
import { paths } from '../routes/paths';
import { useAppSelector } from '../app/hooks';
import wordmark from '../assets/dusq-wordmark.svg';
import { selectUser } from '../store/selectors/auth';
import {
  DropdownMenu,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuContent,
} from '../ui/DropdownMenu';
import {
  adminHomeFor,
  canWorkCoachChat,
  canViewProduction,
  canWorkSupportChat,
  canViewCoachInsights,
  canRunSupportConsole,
  canManageSleepCoaches,
  canManageActivityGroups,
} from '../lib/roles';

export const panelsFor = (user) => {
  const home = adminHomeFor(user);
  return [
    ...(home && home !== paths.dashboard.support.console
      ? [{ to: home, label: 'Back to dashboard' }]
      : []),
    ...(canViewCoachInsights(user)
      ? [{ to: paths.dashboard.insights.root, label: 'Insight Summary' }]
      : []),
    ...(canWorkCoachChat(user)
      ? [{ to: paths.dashboard.coach.console, label: 'Sleep Coach Chat' }]
      : []),
    ...(canWorkCoachChat(user) || canWorkSupportChat(user)
      ? [{ to: paths.dashboard.myProfile, label: 'My profile' }]
      : []),
    ...(canManageActivityGroups(user)
      ? [{ to: paths.dashboard.plan.activity.groups, label: 'Activity groups' }]
      : []),
    ...(canWorkSupportChat(user)
      ? [{ to: paths.dashboard.support.console, label: 'Customer Support' }]
      : []),
    ...(canManageSleepCoaches(user)
      ? [{ to: paths.dashboard.coach.caseload, label: 'Coach Administration' }]
      : []),
    ...(canRunSupportConsole(user)
      ? [{ to: paths.dashboard.ops.now, label: 'Customer Ops Admin' }]
      : []),
    ...(canViewProduction(user)
      ? [{ to: paths.dashboard.production.root, label: 'Production' }]
      : []),
  ];
};

// The wordmark keeps the Figma header footprint and exposes only permitted surfaces.
export const PanelSwitcher = ({ label, className }) => {
  const user = useAppSelector(selectUser);
  const { pathname } = useLocation();
  return (
    <DropdownMenu>
      <DropdownMenuTrigger
        aria-label="Switch panel"
        data-tour="panels"
        className={cn(
          'flex min-w-0 items-center gap-3 rounded focus-visible:outline focus-visible:outline-2 focus-visible:outline-dusq-accent',
          className
        )}
      >
        <img src={wordmark} alt="DUSQ" width={69.334} height={20} className="shrink-0" />
        <span className="truncate text-[12px] text-dusq-grey400">{label}</span>
        <ChevronDown className="h-3 w-3 shrink-0 text-dusq-grey400" />
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="start"
        className="border-dusq-border bg-dusq-grey950 text-dusq-text"
      >
        {panelsFor(user).map(({ to, label: title }) => (
          <DropdownMenuItem key={to} asChild>
            <Link to={to} aria-current={pathname === to ? 'page' : undefined}>
              {title}
            </Link>
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
